// packages/web-app/src/components/auth/AuthProvider.tsx
import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { Auth0Provider, GetTokenSilentlyOptions, useAuth0 } from '@auth0/auth0-react';
import { auth0Config } from '../../auth0-config';
import { useNotification } from '../ui-library/NotificationProvider';
import useAnalytics from '@/hooks/useAnalytics';
import { apiClient } from '@/utils/apiClient';
import { ContextService } from '@/services/contextService';
import { clearVoteMateStorage, clearVoteMateStorageOnLogout } from '@/utils/storageUtils';
import i18n, { changeLanguage, urlLanguageOverride } from '../../i18n';
import { resolveSupportedLanguage } from '@/constants/languages';
import { applyNavModeFromServer } from '@/hooks/useNavMode';
import { answerStyleStore } from '@/hooks/useAnswerStyle';
import { applyAccountAnswerStyle } from '@/services/answerStyleSync';
import { applyAccountAdConsent } from '@/services/adConsentSync';
import { markAdConsentAccountSettled } from '@/utils/adConsentStore';
import LoginModal from './LoginModal';
import SignupModal from './SignupModal';
import { ResearchProvider } from '@/contexts/ResearchContext';
import { isEmbed } from '@/config/embed';
import { getAppUrl } from '@/config/env';
import { completeAuthLogout } from './authLogout';

// sessionStorage key used to persist "user initiated a login" intent across
// Auth0's loginWithRedirect (email magic-link flow). The in-memory ref does
// not survive a full-page navigation, so the redirect path needs durable
// state to know whether to surface failure toasts after the callback returns.
const LOGIN_INTENT_KEY = 'votemate_login_intent';

// Auth0 connection → the method vocabulary 'Login Method Selected' uses, so
// failure events segment on the same values as the selection funnel.
const METHOD_BY_CONNECTION: Record<string, string> = {
  'google-oauth2': 'google',
  facebook: 'facebook',
  windowslive: 'microsoft',
  apple: 'apple',
  email: 'email',
  'Username-Password-Authentication': 'password',
};

// User data structure returned from Auth0
interface User {
  id?: string;
  user_id?: string;
  auth0Id: string; // Canonical Auth0 identifier used by the backend
  name?: string;
  first_name?: string;
  last_name?: string;
  email?: string;
  picture?: string;
  preferredLanguage?: string;
  state?: string;
  zipCode?: string;
  // onLoginCallback spreads the backend profile, which uses snake_case.
  zip_code?: string;
  county?: string;
  initialSetupComplete?: boolean;
  // Research collaboration metadata — persisted at signup when the participant
  // arrived via a research-partner link with the corresponding URL params.
  // Read by ResearchProvider on returning visits to repopulate context.
  // Generic across any external research integration.
  campaign_code?: string;
  participant_id?: string;
}

// Authentication context interface - defines all auth-related functions and state
interface AuthContextType {
  user: User | null; // Current user data (null if not logged in)
  signIn: () => void; // Generic sign-in (shows Auth0 Universal Login)
  // Checked callers receive logout failures; ordinary sign-out stays best effort.
  signOut: (options?: { throwOnError?: boolean }) => Promise<void>;
  // Async gets auth0 JWT. Should only be used by API layer.
  getAccessTokenSilently: (options?: GetTokenSilentlyOptions) => Promise<string>;
  // Clears a stale Auth0 session (cached `isAuthenticated` with no usable
  // refresh token) so the app falls back to a clean guest state. Used by the
  // API layer when a silent token fetch fails. Should only be used there.
  recoverFromStaleSession: () => Promise<void>;
  refreshUserProfile: () => Promise<void>; // Refresh user profile from backend
  // Optimistically update local user state without a backend round-trip
  updateUser: (updates: Partial<User>) => void;
  isAuthenticated: boolean; // Whether user is currently logged in
  isInitializing: boolean;
  logoutLoading: boolean; // Logout process loading state
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

/**
 * Provides auth context to a component or hook. Use `isInitializing` to detect if auth loading is
 * complete. Use `user` for all transactions and rendering in the UI. Use `signIn` for all sign up
 * and sign in needs. `getAccessTokenSilently` is only provided for the API layer to use.
 * Components and hooks otherwise should not access this.
 *
 * @returns {AuthContext} auth context value exposing `user`, `isInitializing`, `signIn()`, and
 * `signOut()`
 */
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

// Main authentication logic component
// Wraps Auth0 functionality and provides our custom auth interface
const Auth0Consumer: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  // Get Auth0 hooks and functions
  const {
    user: auth0User,
    isAuthenticated,
    isLoading: auth0Loading,
    error: auth0Error,
    getAccessTokenSilently,
    loginWithRedirect,
    loginWithPopup,
    logout,
  } = useAuth0();
  const [isLoadingUserProfile, setIsLoadingUserProfile] = useState(false);
  const [isInitializing, setIsInitializing] = useState(true);
  const [user, setUser] = useState<User | null>(null);
  const [hasAttemptedUserProfileLoad, setHasAttemptedUserProfileLoad] = useState(false);
  const { showNotification } = useNotification();
  const analytics = useAnalytics();

  const [logoutLoading, setLogoutLoading] = useState(false);
  const [loginModalOpen, setLoginModalOpen] = useState(false);
  const [signupModalOpen, setSignupModalOpen] = useState(false);
  const activePopupRef = useRef<Window | null>(null);
  // Tracks whether the in-flight profile load was triggered by an explicit
  // user action (Sign In click, popup login, settings save) vs. a passive
  // bootstrap on app open. We only surface error toasts for the former —
  // a generic "Login failed" toast on cold start is misleading because the
  // user did not try to do anything.
  const userInitiatedLoginRef = useRef(false);
  // Guards against firing more than one local logout when a stale session
  // makes several in-flight queries fail at once (each calls
  // getAccessTokenSilently). Reset when the Auth0 subject changes so a later
  // genuine session can recover again.
  const sessionRecoveryRef = useRef(false);

  // Hydrate the intent flag from sessionStorage on mount so the email-login
  // redirect flow (which navigates away and back) still surfaces failures.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    try {
      if (sessionStorage.getItem(LOGIN_INTENT_KEY) === 'true') {
        userInitiatedLoginRef.current = true;
      }
    } catch {
      // sessionStorage may be unavailable (private mode, sandboxed iframe).
    }
  }, []);

  const clearLoginIntent = useCallback(() => {
    userInitiatedLoginRef.current = false;
    try {
      sessionStorage.removeItem(LOGIN_INTENT_KEY);
    } catch {
      // sessionStorage may be unavailable.
    }
  }, []);

  // Synchronize logout across multiple browser tabs
  // When user logs out in one tab, all other tabs will also logout
  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'votemate_logout_event' && e.newValue) {
        // Another tab has logged out, redirect to home
        window.location.href = window.location.origin;
      }
    };

    // Clear VoteMate storage when browser window/tab is closed
    const handleBeforeUnload = () => {
      if (isAuthenticated) {
        // Clear conversation context and VoteMate-specific storage on window close
        clearVoteMateStorage();
      }
    };

    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [isAuthenticated]);

  const onLoginCallback = useCallback(
    (userProfile: any) => {
      // Prefer the user-provided first/last name stored in backend PII (collected at signup
      // or edited in settings). Falls back to the OAuth provider's name when PII is empty.
      const backendFirstName: string = userProfile?.first_name || '';
      const backendLastName: string = userProfile?.last_name || '';
      const resolvedName =
        backendFirstName || backendLastName
          ? `${backendFirstName} ${backendLastName}`.trim()
          : auth0User?.name;

      setUser({
        auth0Id: auth0User?.sub || '',
        name: resolvedName,
        email: auth0User?.email,
        picture: auth0User?.picture,
        ...userProfile,
      });
    },
    [auth0User]
  );

  const updateUser = useCallback((updates: Partial<User>) => {
    setUser((prev) => (prev ? { ...prev, ...updates } : prev));
  }, []);

  const populateUserProfile = useCallback(
    async (prefetchedToken?: string): Promise<void> => {
      setIsLoadingUserProfile(true);
      // Capture the flag at entry — the finally block resets it, but we want
      // the value at the moment we decide whether to surface failures and
      // whether this attempt counts as a user-initiated sign-in.
      const userInitiated = userInitiatedLoginRef.current;
      try {
        // Some social providers (notably Facebook) silently issue tokens with
        // no email claim even when the email scope is requested — typically
        // because the user's account has no verified email on file. We can't
        // proceed without one (email is a hard requirement on signup and the
        // primary key in HubSpot), so abort here with a clear error and ask
        // the user to choose a different sign-in method.
        if (!auth0User?.email) {
          analytics.track('Login Failed', {
            reason: 'no_email_from_provider',
            user_initiated: userInitiated,
          });
          showNotification(i18n.t('errorNoEmailFromProvider', { ns: 'auth' }), 'error');
          await logout({ logoutParams: { returnTo: window.location.origin } });
          return;
        }

        const accessToken = prefetchedToken ?? (await getAccessTokenSilently());
        const answerStyleRevision = answerStyleStore.accountRevision();
        // Call our backend /auth/callback endpoint
        const response = await apiClient('/auth/callback', {
          method: 'POST',
          accessToken,
          body: JSON.stringify({
            user_data: {
              email: auth0User.email,
              name: auth0User?.name,
              picture: auth0User?.picture,
            },
          }),
        });

        const data = await response.json();

        if (data.success) {
          // Apply the account's answer style (and carry an explicit guest choice
          // onto an account without one) before the profile lands, so neither
          // the first chat turn nor the onboarding invitation sees guest state.
          await applyAccountAnswerStyle({
            userId: data.data?.user_id,
            email: auth0User.email,
            accessToken,
            server: data.data ?? {},
            revisionAtRead: answerStyleRevision,
          });
          // An opt-out made on another device applies here, and a choice made in
          // this browser before signing in carries up to an undecided account.
          applyAccountAdConsent({
            email: auth0User.email,
            accessToken,
            account: data.data?.advertising_consent,
          });
          // Store user profile in context
          await onLoginCallback(data.data);
          setLoginModalOpen(false);

          // One completion event for every method — popup (social) and
          // redirect (email/password) alike. Gated on user intent so passive
          // session restores on page load don't count as sign-ins.
          if (userInitiated) {
            analytics.track('Sign in complete');
          }

          // Sync the user's saved language preference into i18n (and localStorage
          // cache). An inbound `?lang=` wins for the session: when one is present we
          // leave the URL language in place and skip applying the account preference
          // (the account value is never overwritten — no settings write happens here).
          //
          // Only apply it for users who have completed signup. A brand-new user's
          // account preference is still the backend default (`en`); applying it here
          // would clobber the language they were using as a guest — both in i18n and
          // in the `vm_language` cache — before the signup form can read and submit it.
          const preferredLanguage = resolveSupportedLanguage(data.data?.preferred_language ?? null);
          if (
            data.data?.initialSetupComplete &&
            !urlLanguageOverride &&
            preferredLanguage &&
            preferredLanguage !== i18n.language
          ) {
            changeLanguage(preferredLanguage);
          }

          // Seed the account's saved mobile-navigation preference so it follows
          // the user across devices. It has no signup step or URL override, so
          // apply whatever the account carries (a no-op when it's unset).
          applyNavModeFromServer((data.data as { nav_mode?: unknown } | undefined)?.nav_mode);

          // Clear guest-built context before init so a stale election can't be read mid-init.
          ContextService.clearContext();
          try {
            await ContextService.initContext(accessToken);
          } catch (contextError) {
            // Non-blocking; context stays cleared, so chat uses no-context, not stale data.
            console.warn('Failed to initialize conversation context:', contextError);
          }
        } else {
          console.error('Backend returned error:', data.message);
          analytics.track('Login Failed', {
            reason: 'backend_rejected',
            user_initiated: userInitiated,
          });
          if (userInitiated) {
            showNotification(i18n.t('errorLoginFailed', { ns: 'auth' }), 'error');
          }
        }
      } catch (error) {
        console.error('Error in login callback:', error);
        analytics.track('Login Failed', {
          reason: 'callback_error',
          user_initiated: userInitiated,
        });
        if (userInitiated) {
          showNotification(i18n.t('errorLoginFailed', { ns: 'auth' }), 'error');
        }
      } finally {
        setIsLoadingUserProfile(false);
        setIsInitializing(false);
        // Reset the intent flag so a later passive bootstrap does not
        // inherit this attempt's "user-initiated" status. Also clears the
        // sessionStorage marker used by the email-redirect flow.
        clearLoginIntent();
      }
    },
    [
      analytics,
      auth0User,
      showNotification,
      onLoginCallback,
      getAccessTokenSilently,
      logout,
      clearLoginIntent,
    ]
  );

  // Recover from a stale Auth0 session: `localstorage` cache makes the SDK
  // rehydrate `isAuthenticated === true` on load, but the matching refresh
  // token can be missing (cleared, rotated past, or never persisted). Any
  // silent token fetch then throws `Missing Refresh Token` / `invalid_grant`,
  // which previously surfaced to guests as "Error Loading Ballot". Clearing
  // the cached session locally (`openUrl: false` skips the jarring full-page
  // Auth0 redirect) flips `isAuthenticated` to false so the app continues in a
  // clean guest state — and the original request can retry without a token.
  const recoverFromStaleSession = useCallback(async (): Promise<void> => {
    if (sessionRecoveryRef.current) return;
    sessionRecoveryRef.current = true;
    answerStyleStore.clearAccount();
    try {
      await logout({ openUrl: false });
    } catch (err) {
      console.warn('Failed to clear stale Auth0 session:', err);
      // Allow a retry if the logout call failed transiently (network blip, etc.)
      // so the next stale-session failure from useApi can attempt recovery again.
      sessionRecoveryRef.current = false;
    }
  }, [logout]);

  const refreshUserProfile = useCallback(async () => {
    if (auth0User && !isLoadingUserProfile) {
      // Callers (Settings save, Signup, Ballot save) invoke this in response
      // to an explicit user action — surface failures to the user.
      userInitiatedLoginRef.current = true;
      await populateUserProfile();
    }
  }, [auth0User, isLoadingUserProfile, populateUserProfile]);

  useEffect(() => {
    // Reset the one-shot profile bootstrap guard when the Auth0 subject changes.
    setHasAttemptedUserProfileLoad(false);
    // Allow a fresh session to recover again later — the previous stale
    // session has been cleared (sub is now different or undefined).
    sessionRecoveryRef.current = false;
  }, [auth0User?.sub]);

  useEffect(() => {
    // Load or refresh the app user profile whenever Auth0 has a user but our app state does not.
    // This covers both the initial redirect callback and in-app popup logins from a guest session.
    if (
      !auth0Loading &&
      !isLoadingUserProfile &&
      auth0User &&
      !user &&
      !hasAttemptedUserProfileLoad
    ) {
      setHasAttemptedUserProfileLoad(true);
      if (!isInitializing) {
        setIsInitializing(true);
      }
      // Validate the refresh token before loading the profile. We pass the
      // resulting token through to populateUserProfile so we don't burn a
      // second silent-auth call (which can race with refresh-token rotation
      // and produce spurious failures).
      getAccessTokenSilently({ cacheMode: 'off' })
        .then((token) => populateUserProfile(token))
        .catch((err: unknown) => {
          const message = err instanceof Error ? err.message : String(err);
          // Auth0 throws a wide variety of error messages here
          // (`login_required`, `invalid_grant`, `Missing Refresh Token`,
          // `Unknown or invalid refresh token`, network errors, etc.). We
          // do not try to enumerate them — any silent-auth failure means
          // we cannot recover this load. Drop into the guest UI; the user
          // can click "Sign In" to start a fresh OAuth flow.
          console.warn('Silent auth failed during bootstrap:', message);
          if (userInitiatedLoginRef.current) {
            // The user just completed a popup/redirect login but we could
            // not exchange tokens — surface something actionable.
            showNotification(i18n.t('errorSessionExpired', { ns: 'auth' }), 'error');
            clearLoginIntent();
          }
          // Clear the stale cached session so `isAuthenticated` flips to false.
          // Without this the SDK keeps reporting an authenticated user, and the
          // next authenticated query (e.g. the guest national ballot) calls
          // getAccessTokenSilently again and fails the same way.
          void recoverFromStaleSession();
          setIsInitializing(false);
        });
    }
  }, [
    auth0Loading,
    isLoadingUserProfile,
    auth0User,
    user,
    hasAttemptedUserProfileLoad,
    isInitializing,
    populateUserProfile,
    getAccessTokenSilently,
    showNotification,
    clearLoginIntent,
    recoverFromStaleSession,
  ]);

  useEffect(() => {
    // Auth0 loading complete and no auth0 user present.
    if (!auth0Loading && isInitializing && !auth0User) {
      if (auth0Error) {
        // SDK init error (typically an expired/invalid refresh token).
        // Drop into the guest UI rather than force-redirecting to the
        // Auth0 universal login screen — the user did not click anything
        // and an unsolicited redirect on app open is jarring. They can
        // click "Sign In" to start a fresh OAuth flow.
        console.warn('Auth0 SDK error during init:', auth0Error.message);
      }
      // Terminal init state with no Auth0 user — populateUserProfile will
      // never run on this load, so its finally-block cleanup won't fire.
      // Drop any pending login intent (e.g. a sessionStorage flag left by a
      // failed email-redirect attempt) so it cannot leak into a later
      // passive bootstrap and surface a misleading "session expired" toast.
      clearLoginIntent();
      setIsInitializing(false);
    }
  }, [auth0Loading, auth0User, auth0Error, isInitializing, clearLoginIntent]);

  // A guest has no account choice for advertising partners to wait on; a
  // signed-in voter's is settled by `applyAccountAdConsent` during sign-in.
  useEffect(() => {
    if (!isInitializing && !isAuthenticated) markAdConsentAccountSettled();
  }, [isInitializing, isAuthenticated]);

  const signIn = () => {
    analytics.track('Sign-in Initiated', undefined, { send_immediately: true });
    // Partner embed runs guest-only. Cross-origin iframe auth (third-party
    // cookies, framed IdP login) is unreliable, so hand off to the full app
    // in a new tab where first-party auth works normally.
    if (isEmbed()) {
      const appUrl = getAppUrl() || window.location.origin;
      window.open(appUrl, '_blank', 'noopener,noreferrer');
      return;
    }
    // Note: we do NOT set userInitiatedLoginRef here. Opening the modal is
    // not yet a login attempt — the user could dismiss it. Intent is set
    // when they actually click a provider (in openLoginPopup or
    // handleModalEmailLogin) so a dismiss-without-trying does not leak a
    // stale "user-initiated" flag into a later passive bootstrap.
    setLoginModalOpen(true);
  };

  const closeLoginModal = useCallback(() => {
    setLoginModalOpen(false);
  }, []);

  const handleLoginModalClose = useCallback(() => {
    if (activePopupRef.current && !activePopupRef.current.closed) return;
    // Modal dismissed (X / backdrop / ESC). Drop any login intent the user
    // accumulated by clicking a provider whose popup was then closed/
    // blocked — they have given up on this attempt, so subsequent passive
    // bootstrap failures should stay silent.
    clearLoginIntent();
    closeLoginModal();
  }, [closeLoginModal, clearLoginIntent]);

  const openPopupWindow = useCallback((width = 450, height = 750) => {
    // Center within the browser window. Use outerWidth/outerHeight so vertical position accounts
    // for tab bar + address bar; innerHeight alone makes the popup sit too high (see screenY).
    const left = Math.max(0, Math.round(window.screenX + (window.outerWidth - width) / 2));
    const top = Math.max(0, Math.round(window.screenY + (window.outerHeight - height) / 2));
    return window.open(
      '',
      'auth0:authorize:popup',
      `width=${width},height=${height},left=${left},top=${top},resizable,scrollbars=yes,status=1`
    );
  }, []);

  const openLoginPopup = useCallback(
    async (connection: string) => {
      // Mark this as a user-initiated login attempt right at the click —
      // not earlier (modal open) and not later (after success). Setting
      // per-click means retries with a different provider in the same
      // modal session still count as user-initiated, even after a previous
      // popup catch fired.
      userInitiatedLoginRef.current = true;

      // Popup auth uses Auth0's ``web_message`` response mode, whose origin is
      // validated against Allowed Web Origins — and that list does not support
      // wildcards. On a dev tunnel (e.g. ``*.trycloudflare.com``) the random
      // origin isn't listed, so fall back to a full-page redirect, which the
      // wildcard Allowed Callback URL already covers. Gated to dev + non-local
      // so production and localhost keep the popup.
      const host = window.location.hostname;
      const onDevTunnel = import.meta.env.DEV && host !== 'localhost' && host !== '127.0.0.1';
      if (onDevTunnel) {
        await loginWithRedirect({ authorizationParams: { connection } });
        return;
      }

      const popup = openPopupWindow();
      activePopupRef.current = popup;
      try {
        await loginWithPopup(
          { authorizationParams: { connection } },
          { popup: popup ?? undefined }
        );
        // Popup auth succeeded. Force the bootstrap effect to fire even if
        // the user signed in with the same Auth0 sub as the stale cached
        // session — the sub-change reset effect won't trigger in that case,
        // and without this we'd be stuck with hasAttemptedUserProfileLoad
        // permanently true and populateUserProfile never running.
        setHasAttemptedUserProfileLoad(false);
      } catch (err) {
        // Popup closed, blocked, or auth failed — modal stays open for retry.
        // Leave userInitiatedLoginRef set so a subsequent provider click
        // within the same modal still surfaces failures. The flag is cleared
        // by populateUserProfile (on the next successful attempt) or by
        // handleLoginModalClose (if the user gives up and dismisses).
        // Surface the error to devtools so unexpected failures (SDK bug,
        // network error) are diagnosable instead of silently swallowed.
        console.warn('Popup auth failed (closed/blocked/error):', err);
        analytics.track('Login Failed', {
          reason: 'popup_closed_or_error',
          user_initiated: true,
          method: METHOD_BY_CONNECTION[connection] ?? connection,
        });
      } finally {
        activePopupRef.current = null;
      }
    },
    [analytics, loginWithPopup, loginWithRedirect, openPopupWindow]
  );

  const handleModalGoogleLogin = useCallback(async () => {
    analytics.track('Login Method Selected', { method: 'google' });
    await openLoginPopup('google-oauth2');
  }, [openLoginPopup, analytics]);

  const handleModalFacebookLogin = useCallback(async () => {
    analytics.track('Login Method Selected', { method: 'facebook' });
    await openLoginPopup('facebook');
  }, [openLoginPopup, analytics]);

  const handleModalMicrosoftLogin = useCallback(async () => {
    analytics.track('Login Method Selected', { method: 'microsoft' });
    await openLoginPopup('windowslive');
  }, [openLoginPopup, analytics]);

  const handleModalAppleLogin = useCallback(async () => {
    analytics.track('Login Method Selected', { method: 'apple' });
    await openLoginPopup('apple');
  }, [openLoginPopup, analytics]);

  const handleModalEmailLogin = useCallback(async () => {
    analytics.track('Login Method Selected', { method: 'email' });
    // Mark this as a user-initiated login attempt at click time, mirroring
    // openLoginPopup. The in-memory ref covers the rare case where
    // loginWithRedirect throws synchronously (or before navigation) and
    // an error path needs to surface a toast in the current render. The
    // sessionStorage write is what survives the actual redirect — the
    // hydration effect on the next mount restores the ref from it.
    userInitiatedLoginRef.current = true;
    try {
      sessionStorage.setItem(LOGIN_INTENT_KEY, 'true');
    } catch {
      // sessionStorage may be unavailable.
    }
    setLoginModalOpen(false);
    try {
      await loginWithRedirect({ authorizationParams: { connection: 'email' } });
    } catch (err) {
      console.warn('Auth0 email redirect failed:', err);
      analytics.track('Login Failed', {
        reason: 'redirect_failed',
        user_initiated: true,
        method: 'email',
      });
      setLoginModalOpen(true);
    }
  }, [loginWithRedirect, analytics]);

  // Username + password login via Auth0 Database connection. Routed to the
  // Universal Login password screen instead of the passwordless email OTP
  // flow. Used by automated browser tools (Playwright, Demosmith) that
  // cannot receive OTP emails. Gated behind VITE_DEBUG in the modal so the
  // entry point never appears in production builds.
  const handleModalPasswordLogin = useCallback(async () => {
    // Defense in depth: the modal only renders the entry point under
    // VITE_DEBUG, but guard the handler too so an accidental future call
    // site (test hook, refactor) can't trigger the password flow in prod.
    if (import.meta.env.VITE_DEBUG !== 'true') return;
    analytics.track('Login Method Selected', { method: 'password' });
    userInitiatedLoginRef.current = true;
    try {
      sessionStorage.setItem(LOGIN_INTENT_KEY, 'true');
    } catch {
      // sessionStorage may be unavailable.
    }
    setLoginModalOpen(false);
    try {
      await loginWithRedirect({
        authorizationParams: { connection: 'Username-Password-Authentication' },
      });
    } catch (err) {
      console.warn('Auth0 password redirect failed:', err);
      analytics.track('Login Failed', {
        reason: 'redirect_failed',
        user_initiated: true,
        method: 'password',
      });
      setLoginModalOpen(true);
    }
  }, [loginWithRedirect, analytics]);

  // Comprehensive logout function that clears all user data and sessions
  // Handles both online and offline scenarios with proper error handling
  const signOut = async (options?: { throwOnError?: boolean }): Promise<void> => {
    if (logoutLoading) {
      if (options?.throwOnError) throw new Error('Logout is already in progress');
      return;
    }

    setLogoutLoading(true);

    // Clear VoteMate-specific client storage before Auth0 logout
    clearVoteMateStorageOnLogout();
    // The account's answer style lived in memory only; the device keeps its own
    // guest preference, never the account's.
    answerStyleStore.clearAccount();

    // Use Auth0 logout when online to properly clear Auth0 session
    try {
      // Notify other browser tabs before redirecting — code after logout() never runs
      localStorage.setItem('votemate_logout_event', Date.now().toString());

      await completeAuthLogout(
        () =>
          logout({
            logoutParams: { returnTo: window.location.origin },
          }),
        options?.throwOnError === true
      );

      // Auth0 logout should redirect away; this return is defensive
      return;
    } catch (auth0Error) {
      console.warn('Auth0 logout failed:', auth0Error);
      if (options?.throwOnError) throw auth0Error;
    } finally {
      setLogoutLoading(false);
    }
  };

  // Address / consent signup modal — authenticated users who have not saved an address yet.
  useEffect(() => {
    if (!isInitializing && isAuthenticated && user && !user.initialSetupComplete) {
      setSignupModalOpen(true);
    } else {
      setSignupModalOpen(false);
    }
  }, [isInitializing, isAuthenticated, user]);

  // Create the context value object with all authentication functions and state
  const value: AuthContextType = {
    user,
    signIn,
    signOut,
    getAccessTokenSilently,
    recoverFromStaleSession,
    refreshUserProfile,
    updateUser,
    isAuthenticated,
    isInitializing,
    logoutLoading,
  };

  return (
    <AuthContext.Provider value={value}>
      <ResearchProvider>
        {children}
        <LoginModal
          open={loginModalOpen}
          onClose={handleLoginModalClose}
          onGoogleLogin={handleModalGoogleLogin}
          onFacebookLogin={handleModalFacebookLogin}
          onMicrosoftLogin={handleModalMicrosoftLogin}
          onAppleLogin={handleModalAppleLogin}
          onEmailLogin={handleModalEmailLogin}
          onPasswordLogin={handleModalPasswordLogin}
        />
        <SignupModal
          open={signupModalOpen}
          onSignupComplete={() => {
            analytics.track('Sign up complete');
          }}
          onDismiss={signOut}
        />
      </ResearchProvider>
    </AuthContext.Provider>
  );
};

// Main AuthProvider component that wraps the entire app
// Provides Auth0 configuration and our custom authentication interface
export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  return (
    <Auth0Provider {...auth0Config}>
      <Auth0Consumer>{children}</Auth0Consumer>
    </Auth0Provider>
  );
};
